import { useAddTrackingNumber } from "@/features/packages/hooks/usePackages";
import { Label } from "@shared/components/ui/label";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@shared/components/ui/drawer";
import { useState } from "react";
import { useIsMobile } from '@shared/hooks/use-mobile';
import { Package } from "lucide-react";
import { useTranslation } from "react-i18next";

const AddTrackingNumber = ({ packageId }) => {
  const { mutate: addTrackingNumber, isPending } = useAddTrackingNumber();
  const { t } = useTranslation();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!trackingNumber.trim()) {
      return;
    }

    addTrackingNumber(
      {
        id: packageId,
        values: {
          trackingNumber: trackingNumber.trim(),
        },
      },
      {
        onSuccess: () => {
          setTrackingNumber("");
          setOpen(false);
        },
      }
    );
  };

  const trigger = (
    <Button variant="outline">
      <Package className="h-4 w-4 mr-2" />
      {t("Add Tracking Number") || "Add Tracking Number"}
    </Button>
  );

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="trackingNumber">
          {t("Tracking Number") || "Tracking Number"}
        </Label>
        <Input
          id="trackingNumber"
          placeholder={t("Enter tracking number") || "Enter tracking number"}
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          disabled={isPending}
        />
      </div>
    </form>
  );

  const footerButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(false)}
        disabled={isPending}
      >
        {t("Cancel") || "Cancel"}
      </Button>
      <Button
        type="submit"
        onClick={handleSubmit}
        disabled={isPending || !trackingNumber.trim()}
      >
        {isPending ? t("Saving...") || "Saving..." : t("Save") || "Save"}
      </Button>
    </>
  );

  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t("Add Tracking Number") || "Add Tracking Number"}
            </DialogTitle>
            <DialogDescription>
              {t("Enter a tracking number for this package") ||
                "Enter a tracking number for this package"}
            </DialogDescription>
          </DialogHeader>
          {formContent}
          <DialogFooter>{footerButtons}</DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("Add Tracking Number") || "Add Tracking Number"}
          </DrawerTitle>
          <DrawerDescription>
            {t("Enter a tracking number for this package") ||
              "Enter a tracking number for this package"}
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-4">{formContent}</div>
        <DrawerFooter>{footerButtons}</DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default AddTrackingNumber;
